import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { IncomeData } from "@/types/fact-finder";

interface IncomeSectionProps {
  data: IncomeData | null;
  hasApplicant2: boolean;
  onSave: (data: IncomeData) => Promise<void>;
  onNext: () => void;
}

const defaultData: IncomeData = {
  applicant1Salary: undefined,
  applicant2Salary: undefined,
  businessProfit: undefined,
  investmentIncome: undefined,
  permanentPensions: undefined,
  otherIncome: undefined,
  otherIncomeDescription: "",
  directDebit: {
    preferredAccount: "new",
    accountName: "",
    bsb: "",
    accountNumber: "",
  },
};

export function IncomeSection({ data, hasApplicant2, onSave, onNext }: IncomeSectionProps) {
  const [formData, setFormData] = useState<IncomeData>(data || defaultData);
  const [isSaving, setIsSaving] = useState(false);

  const formatCurrency = (value: number | undefined): string => {
    if (!value) return "";
    return value.toLocaleString();
  };

  const parseCurrency = (value: string): number | undefined => {
    const parsed = parseInt(value.replace(/,/g, ""), 10);
    return parsed || undefined;
  };

  const updateField = (field: keyof IncomeData, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateDirectDebit = (field: string, value: unknown) => {
    setFormData((prev) => ({
      ...prev,
      directDebit: { ...prev.directDebit!, [field]: value },
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(formData);
      onNext();
    } finally {
      setIsSaving(false);
    }
  };

  // Calculate total annual income
  const totalAnnual =
    (formData.applicant1Salary || 0) +
    (formData.applicant2Salary || 0) +
    (formData.businessProfit || 0) +
    (formData.investmentIncome || 0) +
    (formData.permanentPensions || 0) +
    (formData.otherIncome || 0);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-serif">Income</h2>
        <p className="text-muted-foreground mt-1">
          Provide details of all income sources. All amounts should be annual (before tax).
        </p>
      </div>

      {/* Employment Income */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Employment Income</h3>
        <p className="text-sm text-muted-foreground">
          Enter gross annual salary (before tax). Include regular overtime, allowances, and bonuses.
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="p-4 bg-muted/30 rounded-lg space-y-4">
            <h4 className="font-medium">Applicant 1</h4>
            <div className="space-y-2">
              <label className="text-sm font-medium">Annual Salary ($)</label>
              <Input
                value={formatCurrency(formData.applicant1Salary)}
                onChange={(e) => updateField("applicant1Salary", parseCurrency(e.target.value))}
                placeholder="0"
              />
            </div>
          </div>

          {hasApplicant2 && (
            <div className="p-4 bg-muted/30 rounded-lg space-y-4">
              <h4 className="font-medium">Applicant 2</h4>
              <div className="space-y-2">
                <label className="text-sm font-medium">Annual Salary ($)</label>
                <Input
                  value={formatCurrency(formData.applicant2Salary)}
                  onChange={(e) => updateField("applicant2Salary", parseCurrency(e.target.value))}
                  placeholder="0"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Other Income Sources */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Other Income Sources</h3>
        <p className="text-sm text-muted-foreground">
          All amounts should be annual (per year).
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="p-4 bg-muted/30 rounded-lg space-y-2">
            <label className="text-sm font-medium">Business/Self-Employment Profit ($)</label>
            <Input
              value={formatCurrency(formData.businessProfit)}
              onChange={(e) => updateField("businessProfit", parseCurrency(e.target.value))}
              placeholder="0"
            />
            <p className="text-xs text-muted-foreground">Net profit after business expenses</p>
          </div>

          <div className="p-4 bg-muted/30 rounded-lg space-y-2">
            <label className="text-sm font-medium">Investment Income ($)</label>
            <Input
              value={formatCurrency(formData.investmentIncome)}
              onChange={(e) => updateField("investmentIncome", parseCurrency(e.target.value))}
              placeholder="0"
            />
            <p className="text-xs text-muted-foreground">Dividends, interest, rental income</p>
          </div>

          <div className="p-4 bg-muted/30 rounded-lg space-y-2">
            <label className="text-sm font-medium">Government Pensions/Benefits ($)</label>
            <Input
              value={formatCurrency(formData.permanentPensions)}
              onChange={(e) => updateField("permanentPensions", parseCurrency(e.target.value))}
              placeholder="0"
            />
            <p className="text-xs text-muted-foreground">Only permanent/ongoing benefits</p>
          </div>

          <div className="p-4 bg-muted/30 rounded-lg space-y-2">
            <label className="text-sm font-medium">Other Income ($)</label>
            <Input
              value={formatCurrency(formData.otherIncome)}
              onChange={(e) => updateField("otherIncome", parseCurrency(e.target.value))}
              placeholder="0"
            />
            {(formData.otherIncome || 0) > 0 && (
              <div className="mt-2">
                <label className="text-xs text-muted-foreground">Please describe:</label>
                <Input
                  value={formData.otherIncomeDescription || ""}
                  onChange={(e) => updateField("otherIncomeDescription", e.target.value)}
                  placeholder="e.g. Child support, alimony"
                  className="mt-1"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Income Summary */}
      <div className="p-6 bg-primary/5 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium">Total Annual Income</h3>
            <p className="text-sm text-muted-foreground">Combined income from all sources</p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-semibold">${formatCurrency(totalAnnual)}</p>
            <p className="text-sm text-muted-foreground">
              ~${formatCurrency(Math.round(totalAnnual / 12))}/month
            </p>
          </div>
        </div>
      </div>

      {/* Direct Debit Details */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Direct Debit Details</h3>
        <p className="text-sm text-muted-foreground">
          Choose how you would like to make loan repayments.
        </p>

        <div className="flex gap-4 mb-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="directDebitPreference"
              checked={formData.directDebit?.preferredAccount === "new"}
              onChange={() => updateDirectDebit("preferredAccount", "new")}
              className="w-4 h-4"
            />
            <span className="text-sm">Set up with new loan account</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="directDebitPreference"
              checked={formData.directDebit?.preferredAccount === "existing"}
              onChange={() => updateDirectDebit("preferredAccount", "existing")}
              className="w-4 h-4"
            />
            <span className="text-sm">Use existing bank account</span>
          </label>
        </div>

        {formData.directDebit?.preferredAccount === "existing" && (
          <div className="p-4 bg-muted/30 rounded-lg space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Account Name</label>
                <Input
                  value={formData.directDebit?.accountName || ""}
                  onChange={(e) => updateDirectDebit("accountName", e.target.value)}
                  placeholder="e.g. John Smith"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">BSB</label>
                <Input
                  value={formData.directDebit?.bsb || ""}
                  onChange={(e) => updateDirectDebit("bsb", e.target.value)}
                  placeholder="000-000"
                  maxLength={7}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Account Number</label>
                <Input
                  value={formData.directDebit?.accountNumber || ""}
                  onChange={(e) => updateDirectDebit("accountNumber", e.target.value)}
                  placeholder="Account number"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end pt-4 border-t">
        <Button onClick={handleSave} disabled={isSaving} size="lg">
          {isSaving ? "Saving..." : "Save & Continue"}
        </Button>
      </div>
    </div>
  );
}
